import React from 'react';
import { Show } from '../../../../shared/Show';
import {
  convertToCreateSigningAppointmentRequest,
  CreateSigningAppointmentFormData,
} from './convertToCreateSigningAppointmentRequest';
import { checkForMutationSuccess } from '../../../../../API/checkForMutationSuccess';
import { useCreateMutation } from '../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../types/shared/UUID';
import { LoadingSpinnerTW } from 'tailwindComponents/loadingSpinnerTW/LoadingSpinnerTW';
import { eSigningMethod } from 'types/signingAppointment/eSigningMethod';
import { isDigitalSchedulingOwnerType } from 'types/order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { css } from '@/styled-system/css';
import { Button } from '@zillow/constellation-10';

const canBeMarkedAsDraft = (wizardFormData: CreateSigningAppointmentFormData) => {
  // Guard pattern, drafts are only available for attorney
  if (wizardFormData.signingMethod !== eSigningMethod.attorney) {
    return;
  }

  // case 1, normal appt, not a correction
  if (!wizardFormData.correctionReasons?.length || !wizardFormData.correctionTriggeredById) {
    return !!wizardFormData.isDigitalScheduling && !wizardFormData.isDigitalSchedulingDraft;
  }

  // case 3, we have a correction appt
  // in this scenario the original appt could be already a draft, but we need to show the
  // option this time
  return !!wizardFormData.isDigitalScheduling;
};

enum AppointmentActions {
  APPOINTMENT = 'creating signing appointment.',
  DRAFT = 'successfully saving a draft appointment.',
  EMAIL = 'sending email confirmation to Signer(s).',
}

export const ConfirmationNavigationAndSubmission: React.FC<{
  goBack: () => void;
  wizardFormData: CreateSigningAppointmentFormData;
  onClose: () => void;
}> = ({ goBack, wizardFormData, onClose }) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const isAttorney = wizardFormData.signingMethod === eSigningMethod.attorney;
  const isNotary = wizardFormData.signingMethod === eSigningMethod.notaryOnly;
  const isSigSync = wizardFormData.isSignatureSyncAppointment;
  const isEmailConfirmation =
    (!!wizardFormData?.isDigitalScheduling && isAttorney) ||
    (!!wizardFormData?.isDigitalScheduling && isNotary && isSigSync);
  const isNotaryDigSyncDraft = !isSigSync && isNotary && isDigitalSchedulingOwnerType(wizardFormData.ownerType);
  const isDraftOptionEnabled = canBeMarkedAsDraft(wizardFormData) || isNotaryDigSyncDraft;

  const [createAppointmentMutation, { isLoading }] = useCreateMutation();

  const onCreate = async (markAsDraft: boolean, action: string) => {
    const request = convertToCreateSigningAppointmentRequest(wizardFormData, markAsDraft);
    const result = await createAppointmentMutation({ orderId, data: request });
    if (checkForMutationSuccess(result, action)) {
      onClose();
    }
  };
  return (
    <section className={css({ marginTop: '16', display: 'flex', justifyContent: 'space-between' })}>
      <Button onClick={goBack} tone={'brand'} emphasis={'outlined'} size="sm" disabled={isLoading}>
        Back
      </Button>
      <section className={css({ display: 'flex', justifyContent: 'end', gap: '8' })}>
        <LoadingSpinnerTW isLoading={isLoading}>
          <Show when={isDraftOptionEnabled}>
            <Button
              onClick={() => onCreate(true, AppointmentActions.DRAFT)}
              tone={'brand'}
              emphasis={'outlined'}
              size="sm"
            >
              Save Draft
            </Button>
          </Show>
          <Show when={!isNotaryDigSyncDraft}>
            <Button
              tone={'brand'}
              emphasis={'filled'}
              size="sm"
              onClick={() =>
                onCreate(false, !isEmailConfirmation ? AppointmentActions.APPOINTMENT : AppointmentActions.EMAIL)
              }
            >
              {!isEmailConfirmation ? `Create` : `Send Email`}
            </Button>
          </Show>
        </LoadingSpinnerTW>
      </section>
    </section>
  );
};
